import { useAccount } from "@qw/auth";
import {
  AlbyMenu,
  type AlbyMenuItem,
  ChatToolFilterSheet,
  describeToolFilter,
  getDefaultToolFilter,
  getToolFilter,
  PlaybookRuns,
  type ToolFilter,
  useAlbyMenuItems,
  useUnreadSessions,
} from "@qw/chat";
import { useCreateChatSessionMutation } from "@qw/graphql";
import { MCPPlatform } from "@qw/qw-common";
import {
  Button,
  ButtonToggle,
  cn,
  DropdownMenu,
  DropdownMenuTrigger,
  Icon,
  type MaterialSymbol,
  Sheet,
  SheetContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@qw/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { bookChatSessionsFilter } from "@/chat/book-chat-panel";
import { useChatScope } from "@/chat/use-chat-scope";
import { ChatPosition } from "@/providers/page-provider";
import { usePage } from "@/providers/use-page";

// Keyed by the enum so a new position can't ship without both halves of its
// control. The label is separate from the stored value on purpose: `float`
// persists as `"Floating"`, which every existing broker already holds and which
// renaming would strand, but the dock now opens expanded — so "Floating" is no
// longer the right word for what the broker gets.
const POSITION_OPTIONS: Record<ChatPosition, { icon: MaterialSymbol; label: string }> = {
  [ChatPosition.side]: { icon: "dock_to_left", label: "Sidebar" },
  [ChatPosition.dock]: { icon: "position_bottom_right", label: "Dock" },
};

/**
 * Where a QWIB row can go. A conversation or insured routes by itself; a bare
 * session id does not, because the recent-chat list cannot say whether it is the
 * broker's book chat or a playbook/product authoring chat. Matches what
 * `ChatSessionMenuItem` did before this menu replaced it.
 */
const isAlbyMenuItemNavigable = (item: AlbyMenuItem) =>
  Boolean(item.inboxConversationId || item.insuredId || item.rootChatSessionId);

/**
 * QWIB's single Alby entry point: a combo button whose `smart_toy` half toggles
 * the chat and whose chevron opens the shared menu, with the controls that used
 * to live in the chat header in the menu's footer.
 *
 * The tool-filter and history panels are rendered here rather than inside the
 * menu. A dropdown closes as soon as focus enters a dialog, and a sheet mounted
 * inside its content would go with it.
 */
export const AlbyNavMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAccount();
  const { toast } = useToast();
  const { chatMounted, chatPanel, chatPosition, isChatOpen, rootPath, setChatPosition, setIsChatOpen } = usePage();
  const { conversationId, toolFilterStorageKey } = useChatScope();
  const { activeChatSessionId } = useUnreadSessions();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Latched rather than tracking `isMenuOpen`, so the menu's two queries never
  // run for a broker who doesn't open it (the nav is on every route, including
  // the ones with no chat), and reopening it never flashes a spinner over a list
  // we already have.
  const [hasOpenedMenu, setHasOpenedMenu] = useState(false);
  const [isToolFilterOpen, setIsToolFilterOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const { items, loading } = useAlbyMenuItems({ skip: !hasOpenedMenu });

  // Off a chat there is no per-chat key to edit, so the panel edits the global
  // default — the arm of the tool filter that still applies. `getToolFilter`
  // already falls back to that default, so in-scope reads need no special case.
  //
  // A registered `chatPanel` is one of the authoring editors, which mounts its
  // own chat under `authoring-playbook-*` / `authoring-product-*`. `useChatScope`
  // describes the inbox's chat, not that one, so claiming scope here would write
  // the broker's edit to a key the visible panel never reads.
  const toolFilterKey = chatMounted && !chatPanel ? toolFilterStorageKey : null;
  const [toolFilter, setToolFilter] = useState<ToolFilter | null>(() =>
    toolFilterKey === null ? getDefaultToolFilter() : getToolFilter(toolFilterKey)
  );

  useEffect(() => {
    setToolFilter(toolFilterKey === null ? getDefaultToolFilter() : getToolFilter(toolFilterKey));
  }, [toolFilterKey]);

  // The chat the run history reads. A conversation anchors by itself; anything
  // else needs the foreground session, which only the mounted chat knows.
  const historyAnchorSessionId = conversationId ? null : activeChatSessionId;
  const hasHistoryAnchor = Boolean(conversationId || historyAnchorSessionId);

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
    if (open) {
      setHasOpenedMenu(true);
    }
  }, []);

  // Replace only on an inbox-to-inbox hop, so picking conversation after
  // conversation doesn't fill the back stack. The nav is also mounted on
  // `/playbooks/*` and `/products/*`, where replacing would drop the editor from
  // history and make the menu a one-way trip into the inbox — so from there this
  // pushes, which is what the attention dropdown this menu replaced did.
  const isOnInbox = location.pathname === rootPath || location.pathname.startsWith(`${rootPath}/`);

  const handleSelectItem = useCallback(
    (item: AlbyMenuItem) => {
      // `openChat=true` on every target: the broker picked a chat, so land with
      // it open even if this tab had it closed.
      if (item.inboxConversationId) {
        navigate(
          { pathname: rootPath, search: `?conversationId=${item.inboxConversationId}&openChat=true` },
          { replace: isOnInbox }
        );
        return;
      }

      if (item.insuredId) {
        navigate({ pathname: `${rootPath}/${item.insuredId}`, search: "?openChat=true" });
        return;
      }

      // Only a root an *ask* anchored to is known to be a book chat, and that is
      // the one `BookChatPanel` reads its selection from. `chatSessionId` alone
      // proves nothing: `recentChatSessionsMenuItems` returns every foreground
      // chat, so a playbook or product authoring session lands here looking
      // identical, and deep-linking it would open a different chat than the row
      // the broker picked. `isAlbyMenuItemNavigable` keeps those rows disabled.
      if (item.rootChatSessionId) {
        navigate({ pathname: rootPath, search: `?openChat=true&chatSessionId=${item.rootChatSessionId}` });
      }
    },
    [isOnInbox, navigate, rootPath]
  );

  const [createChatSession, { loading: isCreatingChat }] = useCreateChatSessionMutation();

  // Starting a book chat from the nav, now that the chat header carries no
  // controls of its own. Minted under `bookChatSessionsFilter` so the panel's own
  // query matches it, and `awaitRefetchQueries` so the list holds the new session
  // before the URL points at it — otherwise `resolveChatSelection` finds no match
  // for the id, adopts an older session, and writes that id back over ours.
  const handleCreateChat = useCallback(async () => {
    setIsMenuOpen(false);

    try {
      const { data } = await createChatSession({
        variables: { input: bookChatSessionsFilter(user.id) },
        refetchQueries: ["ChatSessions"],
        awaitRefetchQueries: true,
      });

      const newChatSessionId = data?.createChatSession?.id;
      if (newChatSessionId) {
        // A full query replacement, so landing from a conversation drops its
        // `conversationId` and the book chat is what mounts.
        navigate({ pathname: rootPath, search: `?openChat=true&chatSessionId=${newChatSessionId}` });
      }
    } catch {
      toast({ title: "Couldn't start a new chat", description: "Please try again.", variant: "destructive" });
    }
  }, [createChatSession, navigate, rootPath, toast, user.id]);

  // The menu has to go before a panel opens: the dropdown closes itself the
  // moment focus lands in the sheet, and closing it after the fact steals focus
  // back out of the panel the broker just opened.
  const openToolFilter = useCallback(() => {
    setIsMenuOpen(false);
    setIsToolFilterOpen(true);
  }, []);

  const openHistory = useCallback(() => {
    setIsMenuOpen(false);
    setIsHistoryOpen(true);
  }, []);

  // Picking a position moves the chat, and the dock lands expanded over most of
  // the page — so the menu has to get out of the way to let the broker see what
  // they chose. Both options close it: a radio group where one arm dismisses and
  // the other doesn't is worse than either rule on its own.
  const selectPosition = useCallback(
    (position: ChatPosition) => {
      setIsMenuOpen(false);
      setChatPosition(position);
    },
    [setChatPosition]
  );

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
        {/*
         * The two halves are one control, so the border belongs to the group and
         * the radius with it: each half squares off, and `overflow-hidden` clips
         * an active half's fill back to the rounded corners.
         */}
        {/*
         * Not `ButtonToggle`: its active state adds a filled background, and the
         * group already has a border holding these two together. Active here is
         * carried by the icon alone — `filled` for the weight, `primary` for the
         * colour — so the control reads as one shape whatever state it is in.
         */}
        <div className="flex items-center overflow-hidden rounded-md border border-border">
          <Button
            aria-label="Toggle Alby"
            className={cn("w-8 rounded-none", isChatOpen && "filled")}
            disabled={!chatMounted}
            display="icon"
            onClick={() => setIsChatOpen((open) => !open)}
            size="sm"
            theme={isChatOpen ? "primary" : "default"}
            variant="ghost"
          >
            <Icon icon="smart_toy" />
          </Button>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="Alby menu"
              className={cn("w-5 rounded-none border-l border-border", isMenuOpen && "filled")}
              display="icon"
              size="sm"
              theme={isMenuOpen ? "primary" : "default"}
              variant="ghost"
            >
              <Icon icon="keyboard_arrow_down" />
            </Button>
          </DropdownMenuTrigger>
        </div>
        <AlbyMenu
          align="end"
          isItemNavigable={isAlbyMenuItemNavigable}
          onCreateChat={isCreatingChat ? undefined : handleCreateChat}
          footer={{
            tools: (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Tool access"
                    display="icon"
                    className="w-8"
                    onClick={openToolFilter}
                    size="sm"
                    variant="ghost"
                  >
                    <Icon icon="build" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{describeToolFilter(toolFilter)}</TooltipContent>
              </Tooltip>
            ),
            history: (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Run history"
                    disabled={!hasHistoryAnchor}
                    display="icon"
                    className="w-8"
                    onClick={openHistory}
                    size="sm"
                    variant="ghost"
                  >
                    <Icon icon="history" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Run history</TooltipContent>
              </Tooltip>
            ),
            // Always live: the position is one user-level preference, not a
            // property of whatever chat happens to be mounted.
            position: (
              <div aria-label="Chat position" className="ml-auto flex items-center gap-px" role="radiogroup">
                {Object.values(ChatPosition).map((position) => (
                  <ButtonToggle
                    active={chatPosition === position}
                    aria-checked={chatPosition === position}
                    aria-label={POSITION_OPTIONS[position].label}
                    className="w-8"
                    key={position}
                    onClick={() => selectPosition(position)}
                    role="radio"
                  >
                    <Icon icon={POSITION_OPTIONS[position].icon} />
                  </ButtonToggle>
                ))}
              </div>
            ),
          }}
          items={items}
          loading={loading}
          onSelectItem={handleSelectItem}
        />
      </DropdownMenu>

      <ChatToolFilterSheet
        chatSessionId={toolFilterKey}
        onChange={setToolFilter}
        onOpenChange={setIsToolFilterOpen}
        open={isToolFilterOpen}
        platform={MCPPlatform.INBOX}
        value={toolFilter}
      />

      <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <SheetContent>
          <PlaybookRuns
            chatSessionId={historyAnchorSessionId ?? undefined}
            conversationId={conversationId ?? undefined}
            onRunClick={(runId) => {
              setIsHistoryOpen(false);
              navigate(`/playbooks/runs/${runId}`);
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};
